import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { SVGWrapper } from '../../components'
import { IconClose } from '../../assets/icons'
import { ReactComponent as DeleteIcon } from '../../assets/icons/IconDelete.svg'
import { formatChatTimestamp } from './utils'

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  background: ${p => (p.selected ? (p.theme.primaryLighter ?? '#d4ebf2') : 'transparent')};
  transition: background 0.2s;

  /* Locked while a response streams: non-interactive and dimmed so the user
     can't switch/delete out from under an in-flight stream. */
  ${p => p.locked && 'cursor: default; opacity: 0.55; pointer-events: none;'}

  ${p =>
    p.mobile
      ? `
        /* Mobile sheet: each row is a bordered card, no timestamp. */
        border: 2px solid ${p.theme.primaryLighter ?? '#d4ebf2'};
        min-height: 71px;
        box-sizing: border-box;
        padding: 12px 16px;
      `
      : `
        /* Desktop: fill-swap selection; reveal delete + underline on hover. */
        &:hover {
          background: ${p.selected ? (p.theme.primaryLighter ?? '#d4ebf2') : (p.theme.ternaryColor ?? '#e7f3f9')};
        }
        &:hover .delete-btn {
          opacity: 1;
        }
      `}
`

const TextCol = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`

const Title = styled.div`
  color: ${p =>
    p.selected
      ? (p.theme.primaryColor ?? '#038ab7')
      : p.mobile
        ? (p.theme.darkestGray ?? '#022a3a')
        : (p.theme.mediumGray ?? '#4a606a')};
  font-size: 16px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${p => (p.mobile ? 'normal' : 'nowrap')};

  ${p =>
    p.mobile &&
    `
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    `}

  ${Row}:hover & {
    text-decoration: ${p => (p.mobile ? 'none' : 'underline')};
  }
`

const TimeText = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${p => p.theme.lightestGray ?? '#7a8e96'};
`

// ≥45×45px touch target on mobile; smaller hover target on desktop.
const DeleteButton = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${p => (p.mobile ? '45px' : '30px')};
  height: ${p => (p.mobile ? '45px' : '30px')};
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  opacity: ${p => (p.mobile ? 1 : 0)};
  transition:
    opacity 0.2s,
    background 0.2s;

  ${p =>
    p.mobile &&
    `
      & svg {
        display: block;
        width: 20px;
        height: 20px;
      }
    `}

  &:hover {
    background: ${p => (p.mobile ? 'transparent' : (p.theme.primaryLighter ?? '#d4ebf2'))};
  }
`

const ChatHistoryItem = ({ metadata, selected, mobile, disabled, onSelect, onDelete }) => {
  const title = metadata.title || 'New Chat'
  const timestamp = formatChatTimestamp(metadata.mostRecentMessageTimestamp || metadata.createdAt)

  return (
    <Row
      selected={selected}
      mobile={mobile}
      locked={disabled}
      onClick={disabled ? undefined : () => onSelect(metadata.id)}>
      <TextCol>
        <Title selected={selected} mobile={mobile} title={title}>
          {title}
        </Title>
        {!mobile && timestamp && <TimeText>{timestamp}</TimeText>}
      </TextCol>
      <DeleteButton
        type='button'
        className='delete-btn'
        mobile={mobile}
        disabled={disabled}
        aria-label='Remove chat'
        onClick={e => {
          e.stopPropagation()
          if (disabled) return
          onDelete(metadata.id)
        }}>
        {mobile ? <DeleteIcon /> : <SVGWrapper svg={IconClose} size='small' />}
      </DeleteButton>
    </Row>
  )
}

ChatHistoryItem.propTypes = {
  metadata: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  mobile: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default ChatHistoryItem
